import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { useJumboTheme } from "@jumbo/hooks";
import Div from "@jumbo/shared/Div";
import {
  Breadcrumbs,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { getClients, updateClient } from "app/services/api/management";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Form, Formik, useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import { generateQrCode } from "./QrCodeGenerator";
import jsPDF from "jspdf";
import { toPng } from "html-to-image";

const ClientFormContent = () => {
  const { t } = useTranslation();
  const { values, setFieldValue, isSubmitting } = useFormikContext();
  const [client, setClient] = useState(null);
  const { clientId, employeeId } = useParams();
  const [qrCodeData, setQrCodeData] = useState({
    companyId: null,
    employeeId: null,
  });
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const handleDownloadPdf = () => {
    toPng(qrRef.current).then((dataUrl) => {
      const pdf = new jsPDF();
      pdf.text(`${values.firstName} ${values.lastName}`, 10, 100);
      pdf.addImage(dataUrl, "PNG", 10, 10, 80, 80);
      pdf.save(`${employeeId}.pdf`);
    });

    return (
      <div>
        <div ref={qrRef}>
          {generateQrCode({
            data: qrCodeData,
            size: 180,
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchClient = async () => {
      if (clientId && clientId !== "new") {
        const response = await getClients(clientId);
        setClient(response);
        if (
          response?.employees &&
          response?.employees?.length > 0 &&
          employeeId !== "new"
        ) {
          const employee = response.employees.find(
            (employee) => employee.employeeId === employeeId
          );
          setQrCodeData(employee.qrCode);
          setFieldValue("active", employee.active);
          setFieldValue("firstName", employee.firstName);
          setFieldValue("lastName", employee.lastName);
          setFieldValue("email", employee.email);
          setFieldValue("phone", employee.phone);
          setFieldValue("pinCode", employee.pinCode);
        } else {
          setFieldValue("active", true);
          setFieldValue("firstName", "");
          setFieldValue("lastName", "");
          setFieldValue("email", "");
          setFieldValue("phone", "");
          setPinCode();
        }
      }
    };
    fetchClient();
  }, [clientId, setFieldValue]);

  const handleGeneratePinCode = () => {
    setPinCode();
  };

  const setPinCode = () => {
    setFieldValue("pinCode", `${Math.floor(1000 + Math.random() * 9000)}`);
  };

  return (
    <Form>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={values.active}
                  onChange={(e) => setFieldValue("active", e.target.checked)}
                  name="active"
                  color="primary"
                />
              }
              label={t("pages.dsokCustomers.active")}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="firstName"
              label={t("pages.dsokCustomers.employees.firstName")}
              value={values.firstName}
              onChange={(e) => setFieldValue("firstName", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="lastName"
              label={t("pages.dsokCustomers.employees.lastName")}
              value={values.lastName}
              onChange={(e) => setFieldValue("lastName", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              name="email"
              label={t("pages.dsokCustomers.employees.email")}
              value={values.email}
              onChange={(e) => setFieldValue("email", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              name="phone"
              label={t("pages.dsokCustomers.employees.phone")}
              value={values.phone}
              onChange={(e) => setFieldValue("phone", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="pinCode"
              type="number"
              label={t("pages.dsokCustomers.employees.pinCode")}
              value={values.pinCode}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 4) {
                  setFieldValue("pinCode", value);
                }
              }}
              inputProps={{
                maxLength: 4,
                pattern: "[0-9]*",
                inputMode: "numeric",
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <Button
              size="normal"
              variant="outlined"
              onClick={() => handleGeneratePinCode()}
            >
              {t("pages.dsokCustomers.generatePinCode")}
            </Button>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={2}>
          {employeeId !== "new" && (
            <div ref={qrRef}>
              {generateQrCode({
                data: qrCodeData,
                size: 180,
              })}
            </div>
          )}
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          {employeeId !== "new" && (
            <Button
              size="normal"
              variant="outlined"
              onClick={() => handleDownloadPdf()}
            >
              {t("pages.dsokDetails.clients.downloadQrCode")}
            </Button>
          )}
        </Grid>
        <Grid item xs={12} md={12} lg={12}></Grid>
        <Grid item xs={12} md={12} lg={12}>
          <Div
            sx={{
              mt: 2,
              mb: 1,
              mx: 2,
              gap: "4px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="normal"
              onClick={() =>
                navigate(`/customer/dsok/clients/${clientId}/employees`)
              }
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="normal"
              disabled={isSubmitting}
            >
              {t("buttons.save")}
            </Button>
          </Div>
        </Grid>
      </Grid>
    </Form>
  );
};

const EmployeeForm = () => {
  const { t } = useTranslation();
  const { theme } = useJumboTheme();
  const { clientId, employeeId } = useParams();
  const [client, setClient] = useState(null);
  const [employee, setEmployee] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchClient = async () => {
      const response = await getClients(clientId);
      if (employeeId && employeeId !== "new") {
        const employee = response.employees.find(
          (employee) => employee.employeeId === employeeId
        );
        setEmployee(employee);
        setClient(response);
      } else {
        setClient(response);
        setEmployee(null);
      }
    };
    fetchClient();
  }, [clientId]);
  const navigate = useNavigate();

  const companyName = client?.companyName || "";
  const handleSubmit = async (values, { setSubmitting }) => {
    if (values.active === undefined) {
      values.active = false;
    }
    // update client's object
    // new employee - create new employeeId
    if (employeeId && employeeId === "new") {
      // add new employee to client's employees array
      const newEmployeeId =
        new Date().getTime().toString(36) +
        Math.random().toString(36).substr(2, 9);
      const newEmployee = {
        ...values,
        employeeId: newEmployeeId,
      };
      if (!client.employees) {
        client.employees = [];
      }
      client.employees.push(newEmployee);
    } else {
      // update employee in client's employees array
      const employeeIndex = client.employees.findIndex(
        (employee) => employee.employeeId === employeeId
      );
      values.employeeId = employeeId;
      client.employees[employeeIndex] = values;
    }
    delete client._id;
    await updateClient(clientId, client)
      .then((response) => {
        const message = `${t(
          "pages.dsokCustomers.notifications.employees.success"
        )}`;
        enqueueSnackbar(message, {
          variant: "success",
        });
      })
      .catch((error) => {
        const message = `${t(
          "pages.dsokCustomers.notifications.employees.error"
        )}`;
        enqueueSnackbar(message, {
          variant: "error",
        });
      });
    setSubmitting(false);
    setTimeout(() => {
      navigate(`/customer/dsok/clients/${clientId}/employees`);
    }, 2000);
  };
  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/clients">
          {t("pages.dsokDetails.clients.title")}
        </Link>
        <Link to={`/customer/dsok/clients/${clientId}/employees`}>
          {companyName}
        </Link>
        <Typography color="text.primary">
          {employeeId === "new"
            ? ""
            : employee?.firstName + " " + employee?.lastName}
        </Typography>
      </Breadcrumbs>
      <Div
        sx={{
          display: "flex",
        }}
      >
        <JumboCardQuick
          title={
            <Typography
              component={"div"}
              sx={{
                display: "flex",
                alignItems: "center",
                minHeight: 29,
                [theme.breakpoints.down("md")]: {
                  flexWrap: "wrap",
                },
              }}
            >
              <Typography
                variant={"h4"}
                mb={0}
                sx={{
                  minWidth: 245,
                  [theme.breakpoints.down("md")]: {
                    minWidth: "100%",
                    marginBottom: 2,
                  },
                }}
              >
                {companyName}
              </Typography>
            </Typography>
          }
          headerSx={{
            borderBottom: 1,
            borderBottomColor: "divider",
            "& .MuiCardHeader-action": {
              my: -0.75,
            },
          }}
          sx={{
            flex: 1,
            mr: 2,
          }}
          wrapperSx={{
            p: 1,
            "&:last-child": {
              pb: 2,
            },
            "& .MuiCollapse-entered:last-child": {
              "& .MuiListItemButton-root": {
                borderBottom: 0,
                borderBottomColor: "transparent",
              },
            },
          }}
        >
          <Formik
            initialValues={{
              firstName: employee?.firstName,
              lastName: employee?.lastName,
              email: employee?.email,
              phone: employee?.phone,
              pinCode: employee?.pinCode,
              active: employee?.active,
            }}
            validationSchema={Yup.object().shape({
              pinCode: Yup.string()
                .required(t("pages.dsokCustomers.validation.required"))
                .matches(
                  /^\d{4}$/,
                  t("pages.dsokCustomers.validation.pinCodeMustBe4Digits")
                )
                .length(
                  4,
                  t("pages.dsokCustomers.validation.pinCodeMustBe4Digits")
                ),
              email: Yup.string().email(
                t("pages.dsokCustomers.validation.invalidEmail")
              ),
              firstName: Yup.string().required(
                t("pages.dsokCustomers.validation.required")
              ),
              lastName: Yup.string().required(
                t("pages.dsokCustomers.validation.required")
              ),
            })}
            onSubmit={handleSubmit}
          >
            <ClientFormContent />
          </Formik>
        </JumboCardQuick>
      </Div>
    </>
  );
};

export default EmployeeForm;
